/**
 * Convert a parsed SHX glyph into an SVG `d` attribute string.
 *
 * Coordinate conventions:
 * - Input strokes are in **font units** with the glyph origin at
 *   (0, 0) and +Y pointing up (the same convention AutoCAD uses).
 * - The output `d` string is also in font units. The caller is
 *   expected to apply `scale` / `widthFactor` / cad-mode-flip through
 *   an SVG `transform` attribute on the wrapping `<g>` element.
 * - Only **oblique** shear is applied inside this function, because an
 *   oblique angle cannot be expressed as a simple group transform
 *   without also skewing arc radii; applying it to raw coordinates
 *   keeps arcs circular in glyph space.
 *
 * Shear formula (AutoCAD-compatible):
 *   x' = x + y * tan(obliqueRad)
 *   y' = y
 * `obliqueRad` > 0 slants the glyph to the right (italic).
 *
 * For consecutive line strokes whose `p1` matches the previous `p2` we
 * omit the repeated `M` command and emit only `L` — this keeps path
 * strings compact and matches how AutoCAD itself compacts strokes when
 * writing SVG / PDF exports.
 */

import type { ShxGlyph, ShxStroke } from "./types";

const MAX_FRACTION_DIGITS = 4;

function fmt(value: number): string {
  if (!Number.isFinite(value)) return "0";
  // Avoid "-0"
  if (value === 0) return "0";
  const rounded = Number(value.toFixed(MAX_FRACTION_DIGITS));
  // Trim trailing zeros the cheap way
  return String(rounded);
}

function shear(x: number, y: number, tanOblique: number): [number, number] {
  return [x + y * tanOblique, y];
}

/**
 * Compute the absolute angular sweep of an arc in [0, 2π].
 *
 * For `ccw` arcs the sweep goes from `startAngle` forward; for CW it
 * goes backwards. We need this magnitude to pick the SVG `large-arc`
 * flag (sweep > π).
 */
function arcSweep(
  startAngle: number,
  endAngle: number,
  ccw: boolean
): number {
  const raw = ccw ? endAngle - startAngle : startAngle - endAngle;
  const twoPi = Math.PI * 2;
  let n = raw % twoPi;
  if (n < 0) n += twoPi;
  // Treat angles within 1e-9 of 0 as a full circle for stability.
  return n === 0 ? twoPi : n;
}

/**
 * Encode one stroke as SVG commands. `openSegment` tracks the last
 * emitted point so that sequential collinear strokes can omit the
 * repeated `M`.
 */
function strokeToCommands(
  stroke: ShxStroke,
  tanOblique: number,
  cursor: { x: number; y: number } | null
): { d: string; endX: number; endY: number } {
  if (stroke.kind === "line") {
    const [x1, y1] = shear(stroke.x1, stroke.y1, tanOblique);
    const [x2, y2] = shear(stroke.x2, stroke.y2, tanOblique);
    const needMove =
      cursor === null ||
      Math.abs(cursor.x - x1) > 1e-6 ||
      Math.abs(cursor.y - y1) > 1e-6;
    const command = needMove
      ? `M${fmt(x1)} ${fmt(y1)} L${fmt(x2)} ${fmt(y2)}`
      : `L${fmt(x2)} ${fmt(y2)}`;
    return { d: command, endX: x2, endY: y2 };
  }
  // Arc.
  const startX = stroke.cx + Math.cos(stroke.startAngle) * stroke.r;
  const startY = stroke.cy + Math.sin(stroke.startAngle) * stroke.r;
  const endX = stroke.cx + Math.cos(stroke.endAngle) * stroke.r;
  const endY = stroke.cy + Math.sin(stroke.endAngle) * stroke.r;
  const [sX, sY] = shear(startX, startY, tanOblique);
  const [eX, eY] = shear(endX, endY, tanOblique);
  const sweep = arcSweep(stroke.startAngle, stroke.endAngle, stroke.ccw);
  const largeArc = sweep > Math.PI ? 1 : 0;
  // SVG `sweep-flag`: 1 = CCW-in-user-space, 0 = CW-in-user-space.
  // Our arcs are defined in the SHX (+Y-up) coordinate frame — which
  // the caller later flips with a `scale(1 -1)` group transform for
  // on-screen display. The flag itself stays as-is; the flip handles
  // the visual direction.
  const sweepFlag = stroke.ccw ? 1 : 0;
  const needMove =
    cursor === null ||
    Math.abs(cursor.x - sX) > 1e-6 ||
    Math.abs(cursor.y - sY) > 1e-6;
  const r = fmt(stroke.r);
  const arcCmd = `A${r} ${r} 0 ${largeArc} ${sweepFlag} ${fmt(eX)} ${fmt(eY)}`;
  const d = needMove ? `M${fmt(sX)} ${fmt(sY)} ${arcCmd}` : ` ${arcCmd}`;
  return { d, endX: eX, endY: eY };
}

/**
 * Build an SVG `d` string from a glyph's strokes.
 *
 * @param glyph        parsed glyph
 * @param obliqueRad   italic shear in radians (0 for upright)
 * @returns path `d` attribute string, or `""` for an empty glyph
 */
export function glyphToSvgPath(glyph: ShxGlyph, obliqueRad = 0): string {
  if (glyph.strokes.length === 0) return "";
  const tanOblique = Math.tan(obliqueRad);
  let cursor: { x: number; y: number } | null = null;
  const parts: string[] = [];
  for (const stroke of glyph.strokes) {
    const { d, endX, endY } = strokeToCommands(stroke, tanOblique, cursor);
    parts.push(d);
    cursor = { x: endX, y: endY };
  }
  return parts.join(" ").replace(/\s+/g, " ").trim();
}
